import { FunctionalComponent, h } from '@stencil/core'
import { JSX } from '@stencil/core/internal'
import { createStore } from '@stencil/store'
import Axios from 'axios'
import { noop } from 'lodash-es'

import { ShareUniversalAccessOption } from '../share/universal'
import {
  BooleanPreferenceName,
  IOverridablePreference,
  IPreferences,
  IUpdatePreferencesValue,
  PreferenceName,
  PreferenceValue,
} from '../types/preferences'
import { flashToast } from './toast'

export const PreferencesStore = createStore<IPreferences>({
  auto_end_tagging: null,
  auto_generate_action_items: null,
  auto_record_external: null,
  auto_share: null,
  bot_name: null,
  request_recording_consent: null,
  show_monologue: null,
  updateUrl: null,
  use_bot_image: null,
  bot_chat: null,
})

const { state } = PreferencesStore

export class PreferenceUpdateError<T extends PreferenceName, V extends PreferenceValue> extends Error {
  constructor(public preference: T, public value: IUpdatePreferencesValue<T, V>, ...attrs) {
    super(...attrs)
  }
}

export function setPreferences(preferences: IPreferences) {
  for (const [preference, value] of Object.entries(preferences)) {
    state[preference] = value
  }
}

export function getPreference<T extends PreferenceName>(preference: T): IPreferences[T] {
  return state[preference]
}

export async function updatePreference<T extends PreferenceName, V extends PreferenceValue>(
  preference: T,
  value: IUpdatePreferencesValue<T, V>
) {
  try {
    await Axios.put(state.updateUrl, {
      [preference]: value,
    })
    if (preference === 'auto_record_external') {
      state.auto_record_external.value = value as IOverridablePreference<boolean>['value']
    } else if (preference === 'request_recording_consent') {
      state.request_recording_consent.value = value as IOverridablePreference<boolean>['value']
    } else if (preference === 'default_share_access') {
      state.default_share_access.value = value as IOverridablePreference<ShareUniversalAccessOption>['value']
    } else {
      state[preference] = value as IPreferences[T]
    }
  } catch (e) {
    throw new PreferenceUpdateError(preference, value, e)
  }
}

export const PreferenceToggle: FunctionalComponent<
  { preference: BooleanPreferenceName } & JSX.IntrinsicElements['ui-toggle']
> = ({ preference, onToggled = noop, checked: _checked, ...attrs }) => {
  return (
    <ui-toggle
      {...attrs}
      checked={
        preference === 'auto_record_external' || preference === 'request_recording_consent'
          ? PreferencesStore.state[preference].value
          : PreferencesStore.state[preference]
      }
      onToggled={async (event) => {
        try {
          await updatePreference(preference, event.detail)
          onToggled(event)
        } catch (ex) {
          flashToast({ type: 'alert', text: 'Failed to update preference' })
        }
      }}
    />
  )
}

export const PreferenceToggleEnum: FunctionalComponent<
  {
    preference: PreferenceName
    trueValue: IUpdatePreferencesValue<PreferenceName, string>
    falseValue: IUpdatePreferencesValue<PreferenceName, string>
  } & JSX.IntrinsicElements['ui-toggle']
> = ({ preference, trueValue, falseValue, onToggled = noop, checked: _checked, ...attrs }) => {
  return (
    <ui-toggle
      {...attrs}
      checked={state[preference] === trueValue}
      onToggled={async (event) => {
        try {
          await updatePreference(preference, event.detail ? trueValue : falseValue)
          onToggled(event)
        } catch (ex) {
          flashToast({ type: 'alert', text: 'Failed to update preference' })
        }
      }}
    />
  )
}
